import { useOfferService } from "../Services/OfferService";
import type{ IMarketplaceViewModel, IMarketplaceViewModelDto } from "./IMarketplaceViewModel";
import { CategoryGroupType, MarketplaceCategory, MarketplaceFilterType, OfferTile, type IMarketplaceCategory, type IOfferTile } from "@domain/Marketplace";
import type { MarketplacePanel } from "./MarketplacePanel";
import type { IPreviewOfferList, IPreviewOfferListDto } from "./IPreviewOfferList";
import { resetRequestedMarketplaceCategory, useGetRequestedMarketplaceCategory } from "./useRequestMarketplaceCategory";
import { MarketplaceMode } from "./MarketplaceMode";
import { getSearchBoundary, type GpsCoordinates } from "@services/GpsCoordinates";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { OfferListOrderBy } from "./Api/OfferListOrderBy";

export class MarketplaceViewModel implements IMarketplaceViewModel
{

    activePanel: MarketplacePanel = 'marketplace';
    activeGroup: string | null = null;
    activeCategory: string | null = null;
    filterType: MarketplaceFilterType;
    cardLinkedOnlyEnabled: boolean = false;
    isFilterOpen: boolean = false;
    query?: string;
    activeResults: IOfferTile[] = [];
    activeSubCategories: IMarketplaceCategory[] = [];
    gpsLocation?: GpsCoordinates;
    marketplaceRadiusKm: number;
    totalCount: number = 0; // The total number of offers in the filtered results, irrespective of pagination
    loadedPages: number = 0; // The number of paginated pages that have been loaded
    pageSize: number = 20; // The number of offers to load on paginated pages
    isLoading: boolean = false;

    enableAutomotiveRewards: boolean;
    categories: IMarketplaceCategory[];
    originatorOffers: IPreviewOfferList;
    featureOffers: IPreviewOfferList;
    specialFeatureOffers: IPreviewOfferList;
    latestOffers: IPreviewOfferList;
    offersEndingSoon: IPreviewOfferList;
    giftCardOffers: IPreviewOfferList;

    constructor( model: IMarketplaceViewModelDto, gpsLocation: GpsCoordinates | undefined, offersDistanceKms: number )
    {

        const app = useAppConfiguration();

        this.filterType = MarketplaceFilterType.All;
        this.gpsLocation = gpsLocation;
        this.marketplaceRadiusKm = app.marketplaceRadiusKm;

        this.enableAutomotiveRewards = model.enableAutomotiveRewards;
        this.categories = this.createCategories( model, model.enableAutomotiveRewards );
        this.originatorOffers = this.getPreviewList( model.originatorOffers );
        this.featureOffers = this.getPreviewList( model.featureOffers );
        this.specialFeatureOffers = this.getPreviewList( model.specialFeatureOffers );
        this.latestOffers = this.getPreviewList( model.latestOffers );
        this.offersEndingSoon = this.getPreviewList( model.offersEndingSoon );
        this.giftCardOffers = this.getPreviewList( model.giftCardOffers );

    }

    /**
     * Initialise the model when the page is loaded
     */
    public async initialise()
    {
        // Look for a requested category in local storage (requested from MyGarage or Safety)
        const category = useGetRequestedMarketplaceCategory();
        if( category != null )
        {
            await this.selectCategory( category.group ?? null, category.categoryId ?? null );
        }
    }

    /**
     * Unpacks a preview offer list
     * @param model 
     * @returns 
     */
    private getPreviewList( model: IPreviewOfferListDto ): IPreviewOfferList
    {
        return {
            offers: model.offers.map( o => new OfferTile( o ) ),
            totalCount: model.totalCount
        }
    }

    /**
     * Resets the marketplace back to the default mode
     */
    public reset()
    {
        this.query = '';
        this.selectCategory( null, null );
        resetRequestedMarketplaceCategory( false );
    }

    /**
     * True if the active results has a filter to be applied
     */
    get hasFilter()
    {
        return this.filterType != MarketplaceFilterType.All || this.cardLinkedOnlyEnabled;
    }

    /**
     * Gets the current mode of the marketplace based on search and selected category states
     */
    get mode()
    {

        if( this.query != null )
        {
            return MarketplaceMode.Search;
        }

        else if( this.activeGroup == 'nearest' )
        {
            return MarketplaceMode.OffersNearMe;
        }

        else if( this.activeGroup == 'automotive' && this.activeCategory == null )
        {
            return MarketplaceMode.SpecialCategory;
        }

        else if( this.activeGroup == 'automotive' && this.activeCategory != null )
        {
            return MarketplaceMode.SubCategory;
        }

        else if( this.activeGroup != null && this.activeCategory != null )
        {
            return MarketplaceMode.Category
        }

        return MarketplaceMode.Default;

    }

    /**
     * Gets the filtered to sub category if there is one
     */
    get filteredSubCategory()
    {
        if( this.mode != MarketplaceMode.SubCategory ) return null;
        return this.activeSubCategories.find( c => c.categoryId == this.activeCategory ) ?? null;
    }

    /**
     * 
     */
    get hasSearchBoundary()
    {
        return this.getSearchBoundary() != undefined;
    }

    /**
     * Create the categories for the marketplace
     * @param model 
     * @returns 
     */
    private createCategories( model: IMarketplaceViewModelDto, enableAutomotiveRewards: boolean )
    {

        // Automotive is a special category with sub categories, so combine
        // all the automotive categories under a single category and sum the offer count
        MarketplaceCategory.Automotive.subCategories = model.categories
            .filter( c => c.categoryGroupName.toLowerCase() == 'automotive' )
            .sort( ( a, b ) => a.ordinal - b.ordinal )
            .map( c => new MarketplaceCategory( c ) );
        
        MarketplaceCategory.Automotive.offersCount = MarketplaceCategory.Automotive
            .subCategories
            .reduce( ( a, c ) => a += c.offersCount, 0 );
        
        // Create the lifestyle categories
        const lifestyle = model.categories
            .filter( c => c.categoryGroupName.toLowerCase() != 'automotive' )
            .sort( ( a, b ) => a.ordinal - b.ordinal )
            .map( c => new MarketplaceCategory( c ) );

        // Combine the results
        return [ ...( enableAutomotiveRewards && MarketplaceCategory.Automotive.offersCount > 0 ? [ MarketplaceCategory.Automotive ] : [] ), ...lifestyle ];

    }

    /**
     * Gets the GPS search from location and the distance of the search boundary.
     */
    public getSearchBoundary()
    {
        return getSearchBoundary( this.gpsLocation, this.marketplaceRadiusKm );
    }

    /**
     * Selects the category or deselects the category if null is passed
     * @param group 
     * @param categoryId 
     */
    async selectCategory( group: string | null, categoryId: string | null )
    {
        await this.loadOffers( group, categoryId );
    }

    /**
     * Loads offers on category change or when load more is pressed
     * @param group 
     * @param categoryId 
     * @param nextPage 
     */
    async loadOffers( group: string | null, categoryId: string | null, loadMore: boolean = false )
    {

        this.isLoading = true;
        this.loadedPages = loadMore ? this.loadedPages += 1 : 0;

        // Null category, reset and return to the configuration for the marketplace landing page
        if( group == null && categoryId == null ) 
        {
            this.query = undefined;
            this.activeResults = [];
            this.activeSubCategories = [];
            this.totalCount = 0 ;
            this.activeGroup = null;
            this.activePanel = 'marketplace';
        }

        // Offers near me
        else if( group == 'nearest' )
        {
            const service = useOfferService();
            const result = await service.getOffersForCategory( this.getSearchBoundary(), CategoryGroupType.Lifestyle, undefined, undefined, OfferListOrderBy.ClosestFirst, this.loadedPages, this.pageSize );
            this.totalCount = result.count;
            this.activeResults = [ ...( loadMore ? this.activeResults : [] ), ...result.offers ];
            this.activeSubCategories = [];
            this.activeGroup = 'nearest';
            this.activePanel = 'nearest';
        }

        // Automotive, filtered to a single catgegory
        else if( group == 'automotive' && categoryId != null )
        {
            const service = useOfferService();
            const result = await service.getOffersForCategory( this.getSearchBoundary(), CategoryGroupType.Automotive, categoryId, undefined, OfferListOrderBy.NewestFirst, this.loadedPages, 1000 );
            this.totalCount = result.count;
            this.activeSubCategories = MarketplaceCategory.Automotive.subCategories.filter( c => c.categoryId == categoryId );
            this.activeResults = [ ...( loadMore ? this.activeResults : [] ), ...result.offers ];
            this.activeGroup = 'automotive';
            this.activePanel = 'category';
        }

        // Automotive, but not filtered, so show all automotive offers in sub categories
        else if( group == 'automotive' && categoryId == null )
        {
            const service = useOfferService();
            const result = await service.getOffersForCategory( this.getSearchBoundary(), CategoryGroupType.Automotive, undefined, undefined, OfferListOrderBy.NewestFirst, this.loadedPages, 1000 );
            this.totalCount = result.count;
            this.activeSubCategories = MarketplaceCategory.Automotive.subCategories;
            this.activeResults = [ ...( loadMore ? this.activeResults : [] ), ...result.offers ];
            this.activeGroup = 'automotive';
            this.activePanel = 'category';
        }

        // Otherwise fetch the lifestyle offers that match the category
        else
        {
            const service = useOfferService();
            const result = await service.getOffersForCategory( this.getSearchBoundary(), CategoryGroupType.Lifestyle, categoryId!, undefined, OfferListOrderBy.NewestFirst, this.loadedPages, 1000 );
            this.totalCount = result.count;
            this.activeSubCategories = [];
            this.activeResults = [ ...( loadMore ? this.activeResults : [] ), ...result.offers ];
            this.activeGroup = 'lifestyle';
            this.activePanel = 'category';
        }

        // Set the category configuration
        this.activeCategory = categoryId;
        this.query = undefined;
        this.isLoading = false;
        
    }

    /**
     * Sets the filter type on the model
     * @param filterType 
     */
    setFilterType( filterType: MarketplaceFilterType )
    {
        this.filterType = filterType;
        if( this.filterType == MarketplaceFilterType.All )
        {
            this.cardLinkedOnlyEnabled = false;
        }

        else if( this.filterType == MarketplaceFilterType.InStore )
        {
            this.cardLinkedOnlyEnabled = true;
        }
    }

    /**
     * Toggles the state of the card linked only filter
     */ 
    toggleCardLinkedOnlyEnabled()
    {
        this.cardLinkedOnlyEnabled = !this.cardLinkedOnlyEnabled;
    }

    /**
     * Performs a search
     */
    async search( query?: string )
    {

        const hasQuery = ( query ?? '' ).length > 0;

        this.query = query;
        this.activePanel = hasQuery ? 'search' : 'marketplace';
        if( hasQuery == false ) return;

        const service = useOfferService();
        this.activeResults = await service.getOffersForQuery( this.getSearchBoundary(), query! );
        this.totalCount = this.activeResults.length;
        this.activeSubCategories = [];
        this.activeGroup = null;

    }

}